"use client";

import { useState, useEffect, Suspense, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getLastOrganization } from "@/lib/preferences";
import { Logo } from "@/components/logo";
import { BRAND } from "@/lib/brand";
import { trackLead } from "@/lib/meta-pixel";

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect");
  const prefilledEmail = searchParams.get("email");

  // Magic link params
  const magicToken = searchParams.get("token");
  const magicId = searchParams.get("id");
  const message = searchParams.get("message");

  const [signingIn, setSigningIn] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  // Soft, non-error hint for expected-flow states like a cancelled sign-in
  // popup — surfaces "you can try again" without looking like a failure.
  // (LB-224)
  const [hint, setHint] = useState("");

  // Magic link state
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [verifyingMagicLink, setVerifyingMagicLink] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);

  const redirectingRef = useRef(false);
  const redirectAttempts = useRef(0);
  const magicLinkVerifiedRef = useRef(false);
  const {
    signInWithGoogle,
    signInWithEmail,
    createUserWithEmail,
    sendMagicLink,
    signInWithMagicLink,
    user,
    loading: authLoading,
    signOut,
    hasAppAccess,
    accessLoading,
  } = useAuth();

  // Check if we're on localhost or staging (for testing)
  // Also allow password auth if NEXT_PUBLIC_ENABLE_PASSWORD_AUTH is set (for Slack app review)
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.includes("staging") ||
      process.env.NEXT_PUBLIC_ENABLE_PASSWORD_AUTH === "true");

  // Check if we're in production (not localhost and not staging)
  const isProduction =
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1" &&
    !window.location.hostname.includes("staging");

  // Handle magic link verification
  useEffect(() => {
    if (magicToken && magicId && !magicLinkVerifiedRef.current && !user) {
      magicLinkVerifiedRef.current = true;
      setVerifyingMagicLink(true);

      // Clear the URL params immediately to prevent re-verification on refresh
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("token");
      newUrl.searchParams.delete("id");
      window.history.replaceState({}, "", newUrl.toString());

      signInWithMagicLink(magicToken, magicId)
        .then((result) => {
          // If there's a redirect URL from the magic link, use it
          if (result?.redirectUrl && result.redirectUrl !== "/") {
            // Extract the path from the full URL if needed
            try {
              const url = new URL(result.redirectUrl);
              const redirectPath = url.pathname + url.search;
              router.push(redirectPath);
            } catch {
              // If it's already a path, use it directly
              if (result.redirectUrl.startsWith("/")) {
                router.push(result.redirectUrl);
              }
            }
          }
          // Otherwise, the AuthContext redirect effect will handle navigation
        })
        .catch((error) => {
          console.error("[Auth] Magic link verification failed:", error);
          setError(
            error?.message?.includes("expired")
              ? "This magic link has expired. Please request a new one."
              : error?.message?.includes("invalid")
                ? "This magic link is invalid. Please request a new one."
                : "Failed to sign in with magic link. Please try again."
          );
          magicLinkVerifiedRef.current = false;
        })
        .finally(() => {
          setVerifyingMagicLink(false);
        });
    }
  }, [magicToken, magicId, signInWithMagicLink, user]);

  useEffect(() => {
    // Wait for both auth and access check to complete
    if (user && !authLoading && !accessLoading && !redirectingRef.current) {
      // Safari fix: Detect redirect loops caused by session cookie issues
      // If we've been redirected back to /auth multiple times while logged in,
      // Safari's ITP is likely blocking our session cookies
      if (
        typeof window !== "undefined" &&
        window.location.search.includes("redirect=")
      ) {
        redirectAttempts.current += 1;

        if (redirectAttempts.current > 2) {
          console.error(
            "[Auth] Redirect loop detected - session cookies may be blocked"
          );
          setError(
            "Authentication error: Your browser may be blocking required cookies. Please check your browser settings and try again, or try a different browser."
          );
          // Sign out to break the loop
          signOut().catch(console.error);
          return;
        }
      }

      redirectingRef.current = true;

      // Validate and sanitize redirect URL to prevent open redirect attacks
      const getRedirectUrl = () => {
        if (
          !redirect ||
          redirect === "/auth" ||
          redirect.startsWith("/auth?")
        ) {
          return null;
        }

        // Only allow relative URLs (must start with /) and prevent protocol handlers
        if (!redirect.startsWith("/") || redirect.startsWith("//")) {
          console.warn("[Auth] Blocked potentially unsafe redirect:", redirect);
          return null;
        }

        // Prevent javascript: and data: URIs
        if (
          redirect.toLowerCase().includes("javascript:") ||
          redirect.toLowerCase().includes("data:")
        ) {
          console.warn("[Auth] Blocked malicious redirect:", redirect);
          return null;
        }

        return redirect;
      };

      const performRedirect = async () => {
        const safeRedirect = getRedirectUrl();

        // Safari fix: Ensure session cookie exists before redirecting
        // Safari's ITP can delete cookies set during cross-origin auth flows
        try {
          const idToken = await user.getIdToken();

          const response = await fetch("/api/auth/session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ idToken }),
          });

          if (!response.ok) {
            console.warn(
              "[Auth] Failed to create session cookie, retrying...",
              response.status
            );
            // Retry once after a brief delay
            await new Promise((resolve) => setTimeout(resolve, 500));
            const retryResponse = await fetch("/api/auth/session", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ idToken }),
            });

            if (!retryResponse.ok) {
              throw new Error(
                `Session cookie creation failed after retry: ${retryResponse.status}`
              );
            }
          }

          // Small delay to ensure cookie is persisted before redirect (especially in Safari)
          await new Promise((resolve) => setTimeout(resolve, 150));
        } catch (error) {
          console.error("[Auth] Session cookie creation failed:", error);
          setError("Failed to create session. Please try signing in again.");
          redirectingRef.current = false;
          return;
        }

        // Check if user has app access
        // Allow through if: redirect contains invite code, or user has access
        const hasInviteCode =
          safeRedirect?.includes("invite=") ||
          safeRedirect?.includes("/invite/");

        // Don't redirect to access-pending if user doesn't have access yet
        // Instead, let them proceed to orgs/select which will handle the access check
        // This prevents the flash of "Request Access" screen for users who DO have access
        // but whose access check is still loading
        if (!hasAppAccess && !hasInviteCode) {
          // Route to orgs/select which will do a proper access check with loading state
          router.push("/orgs/select");
          return;
        }

        if (safeRedirect) {
          router.push(safeRedirect);
        } else {
          const lastOrg = getLastOrganization();
          if (lastOrg) {
            router.push(`/@${lastOrg.slug}/feed`);
          } else {
            router.push("/orgs/select");
          }
        }
      };

      performRedirect();
    }
  }, [
    user,
    redirect,
    router,
    authLoading,
    accessLoading,
    hasAppAccess,
    signOut,
  ]);

  const handleGoogleSignIn = async () => {
    try {
      setSigningIn(true);
      setError("");
      setHint("");
      await signInWithGoogle();
      // Fire Lead event for Google sign-ups
      trackLead();
    } catch (error: any) {
      console.error("Error signing in with Google:", error);
      const code = error?.code;

      // Popups are blocked in in-app browsers (Instagram, Facebook, TikTok) and
      // some mobile Safari configurations. Recover by switching to magic link.
      if (code === "auth/popup-blocked") {
        setShowEmailAuth(true);
        setError(
          "Pop-up was blocked. Please use the magic link option below to sign in.",
        );
      } else if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        // User dismissed the popup themselves — not an error, but leaving them
        // with a silent failed click reads as "the button is broken." Nudge
        // them with a soft, non-red hint that a retry is expected. (LB-224)
        setError("");
        setHint("Sign-in cancelled — try again when you're ready.");
      } else {
        setError("Failed to sign in with Google. Please try again.");
      }
      setSigningIn(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setHint("");

    if (!email || !password) {
      console.warn("[Auth] Missing email or password");
      setError("Please enter both email and password");
      return;
    }

    if (isSignUp && !displayName) {
      console.warn("[Auth] Missing display name for signup");
      setError("Please enter your name");
      return;
    }

    try {
      setSigningIn(true);

      if (isSignUp) {
        await createUserWithEmail(email, password, displayName);
        trackLead();
      } else {
        await signInWithEmail(email, password);
      }

      // Don't reset signingIn here - let the redirect handle it
    } catch (error: any) {
      console.error("[Auth] Error with email auth:", error);
      console.error("[Auth] Error code:", error?.code);
      console.error("[Auth] Error message:", error?.message);

      if (error?.code === "auth/email-already-in-use") {
        setError("This email is already in use");
      } else if (error?.code === "auth/weak-password") {
        setError("Password should be at least 6 characters");
      } else if (error?.code === "auth/user-not-found") {
        setError("No account found with this email");
      } else if (error?.code === "auth/wrong-password") {
        setError("Incorrect password");
      } else if (error?.code === "auth/invalid-email") {
        setError("Invalid email address");
      } else {
        setError(error?.message || "Authentication failed. Please try again.");
      }
      setSigningIn(false);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setSendingMagicLink(true);

      // Include the current origin so the magic link brings them back to the same environment
      // This enables magic link to work from localhost
      const currentOrigin =
        typeof window !== "undefined" ? window.location.origin : "";
      const redirectUrl = currentOrigin + (redirect || "/");
      await sendMagicLink(email, redirectUrl);

      setMagicLinkSent(true);
    } catch (error: any) {
      console.error("[Auth] Error sending magic link:", error);
      setError(
        error?.message || "Failed to send magic link. Please try again."
      );
    } finally {
      setSendingMagicLink(false);
    }
  };

  // Show loading state while redirecting (prevents flash of login form)
  if (user && !authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Logo size="xl" className="text-gray-900 mb-6 animate-pulse" />
        <p className="text-sm text-gray-500">Signing you in...</p>
      </div>
    );
  }

  // Show loading during initial auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Logo size="xl" className="text-gray-900 animate-pulse" />
      </div>
    );
  }

  // Show loading while verifying magic link
  if (verifyingMagicLink) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Logo size="xl" className="text-gray-900 mb-6 animate-pulse" />
        <p className="text-sm text-gray-500">Verifying your magic link...</p>
      </div>
    );
  }

  // Show magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row bg-white">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 md:py-0">
          <div className="w-full max-w-[340px] text-center">
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-7 h-7 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Check your email
            </h2>
            <p className="text-sm text-gray-500 mb-8">
              We sent a link to{" "}
              <span className="text-gray-900 font-medium">{email}</span>
              <br />
              Click the link to sign in.
            </p>
            <button
              onClick={() => {
                setMagicLinkSent(false);
                setEmail("");
              }}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Use a different email
            </button>

            <div className="mt-12 text-sm text-gray-500">
              <Link href="/" className="hover:text-gray-900 transition-colors">
                ← Back to homepage
              </Link>
            </div>
          </div>
        </div>
        {/* Keep the dark panel visible during magic link sent state */}
        <div className="hidden md:flex md:w-[480px] lg:w-[520px] bg-gray-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Left side — Auth form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 md:py-0">
        <div className="w-full max-w-[340px]">
          {/* Logo and branding */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <Logo size="xl" className="text-gray-900" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-2">
              {BRAND.name}
            </h1>
            <p className="text-sm text-gray-500">
              {prefilledEmail
                ? "Sign in to accept your invitation"
                : showEmailAuth
                  ? "Enter your email to continue"
                  : BRAND.tagline}
            </p>
          </div>

          {message === "reauth-required" && (
            <div className="mb-6 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-sm text-amber-800 text-center">
                For security, please sign in again to complete your request.
                You&apos;ll be returned to where you left off.
              </p>
            </div>
          )}

          {error && (
            <div
              className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg"
              data-testid="auth-error"
            >
              <p className="text-sm text-red-700 text-center">{error}</p>
            </div>
          )}

          {hint && !error && (
            <div
              className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg"
              data-testid="auth-hint"
            >
              <p className="text-sm text-gray-600 text-center">{hint}</p>
            </div>
          )}

          {showEmailAuth ? (
            <div className="space-y-4">
              {/* Email input - always shown */}
              <div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent focus:bg-white transition-colors"
                  placeholder="Email address"
                  disabled={signingIn || sendingMagicLink}
                  data-testid="email-input"
                  autoFocus
                />
              </div>

              {/* Local/Staging: Show password fields */}
              {isLocalhost && (
                <>
                  {isSignUp && (
                    <div>
                      <input
                        id="displayName"
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent focus:bg-white transition-colors"
                        placeholder="Your name"
                        disabled={signingIn}
                        data-testid="display-name-input"
                      />
                    </div>
                  )}
                  <div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent focus:bg-white transition-colors"
                      placeholder="Password"
                      disabled={signingIn}
                      data-testid="password-input"
                    />
                  </div>
                  <Button
                    onClick={handleEmailAuth}
                    disabled={signingIn || authLoading}
                    className="w-full h-12 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors"
                    size="lg"
                    data-testid={isSignUp ? "sign-up-button" : "sign-in-button"}
                  >
                    {isSignUp ? "Create account" : "Sign in"}
                  </Button>
                  <div className="relative my-1">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-100"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-3 bg-white text-gray-400">or</span>
                    </div>
                  </div>
                </>
              )}

              {/* Magic link button - shown on all environments */}
              <Button
                onClick={handleSendMagicLink}
                disabled={sendingMagicLink || signingIn || authLoading || !email}
                variant={isLocalhost ? "outline" : "default"}
                className={`w-full h-12 text-sm font-medium rounded-lg transition-colors ${
                  isLocalhost
                    ? "border-gray-200 text-gray-700 hover:bg-gray-50"
                    : "bg-gray-900 hover:bg-gray-800 text-white"
                }`}
                size="lg"
                data-testid="send-magic-link-button"
              >
                {sendingMagicLink ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Sending...
                  </>
                ) : (
                  "Send magic link"
                )}
              </Button>

              {/* Footer links */}
              <div className="flex items-center justify-center gap-4 text-sm pt-4">
                {isLocalhost && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignUp(!isSignUp);
                      setError("");
                    }}
                    className="text-gray-500 hover:text-gray-900 transition-colors"
                    disabled={signingIn}
                  >
                    {isSignUp ? "Sign in instead" : "Create account"}
                  </button>
                )}
                {isLocalhost && <span className="text-gray-300">·</span>}
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailAuth(false);
                    setError("");
                  }}
                  className="text-gray-500 hover:text-gray-900 transition-colors"
                  disabled={signingIn || sendingMagicLink}
                >
                  Back
                </button>
              </div>

              <div className="mt-8 text-center text-sm text-gray-500">
                <Link href="/" className="hover:text-gray-900 transition-colors">
                  ← Back to homepage
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                onClick={handleGoogleSignIn}
                disabled={signingIn || authLoading || sendingMagicLink}
                className="w-full h-12 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors"
                size="lg"
                data-testid="google-sign-in"
              >
                <svg className="mr-2.5 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              <Button
                onClick={() => setShowEmailAuth(true)}
                disabled={signingIn || authLoading}
                variant="outline"
                className="w-full h-12 text-sm font-medium rounded-lg border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                size="lg"
                data-testid="email-auth-button"
              >
                Continue with email
              </Button>
            </div>
          )}

          {/* Footer links */}
          <div className="mt-12 text-center text-sm text-gray-500">
            <Link href="/" className="hover:text-gray-900 transition-colors">
              ← Back to homepage
            </Link>
          </div>
        </div>
      </div>

      {/* Right side — Value reinforcement (hidden on mobile, shown on md+) */}
      <div className="hidden md:flex md:w-[480px] lg:w-[520px] bg-gray-900 text-white flex-col justify-center px-12 lg:px-16">
        <div>
          <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
            Set up in 2 minutes
          </p>
          <h2 className="text-2xl lg:text-3xl font-bold mb-8 leading-tight">
            Less reporting, more designing.
          </h2>

          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-white text-sm">Free for small teams</p>
                <p className="text-sm text-gray-400">Unlimited members, no credit card</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-white text-sm">Post from Figma</p>
                <p className="text-sm text-gray-400">Share work without switching tabs</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-white text-sm">AI does the reporting</p>
                <p className="text-sm text-gray-400">Standup summaries, rollups, and design reviews</p>
              </div>
            </div>
          </div>

          {/* Trust logos */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-xs text-gray-500 mb-4">Trusted by design teams at</p>
            <div className="flex items-center gap-6">
              {[
                { domain: 'stripe.com', alt: 'Stripe' },
                { domain: 'mavenclinic.com', alt: 'Maven Clinic' },
              ].map(({ domain, alt }) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={domain}
                  src={`/api/logo?domain=${domain}`}
                  alt={alt}
                  className="h-5 max-w-[100px] object-contain opacity-40 brightness-200 grayscale"
                />
              ))}
              <span className="text-xs text-gray-600">and others</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile value banner (shown on small screens only) */}
      <div className="md:hidden bg-gray-50 border-t border-gray-200 px-6 py-6">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Free for small teams
          </span>
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Post from Figma
          </span>
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            AI does the reporting
          </span>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-white">
          <Logo size="xl" className="text-gray-900 animate-pulse" />
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
